import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Grid,
  Chip,
  InputAdornment,
  Alert,
} from "@mui/material";
import { Add, Remove, Person, ChildCare, Warning } from "@mui/icons-material";
import useStyle from "./styles";
import { TooltipInformacao, PositionTooltip } from "../TooltipInformacao";

const initPassageiro = {
  idade: "",
};

function PassageiroList({ onChange, compact = true }) {
  const cssStyle = useStyle();
  const [passageiros, setPassageiros] = useState([initPassageiro]);

  // Notificar o componente pai sobre mudanças
  useEffect(() => {
    onChange(passageiros);
  }, [passageiros]);

  const adicionarPassageiro = () => {
    if (passageiros.length >= 7) return;
    setPassageiros([...passageiros, initPassageiro]);
  };

  const removerPassageiro = () => {
    if (passageiros.length <= 1) return;
    const novosPassageiros = passageiros.slice(0, -1);
    setPassageiros(novosPassageiros);
  };

  const handlerChangePassageiro = (index, event) => {
    const valor = event.target.value.replace(/\D/g, ""); // Remove não-números
    if (valor === "" || (parseInt(valor) >= 0 && parseInt(valor) <= 99)) {
      const novosPassageiros = passageiros.map((p, i) =>
        i === index ? { ...p, idade: valor } : p
      );
      setPassageiros(novosPassageiros);
    }
  };

  const getDescricaoPassageiros = () => {
    const count = passageiros.filter((p) => p.idade !== "").length;
    if (count === 0) return "Selecionar passageiros";
    if (count === 1) return "1 passageiro";
    return `${count} passageiros`;
  };

  const passageirosComIdade = passageiros.filter((p) => p.idade !== "").length;
  const totalPassageiros = passageiros.length;
  const todosPreenchidos = passageirosComIdade === totalPassageiros;

  // Se não for compact, mostra versão expandida (para compatibilidade)
  if (!compact) {
    return (
      <Box className={cssStyle.containerSelecao}>
        <div className={cssStyle.boxSelecao}>
          <div className={cssStyle.boxInformativo}>
            <TextField
              spellCheck="false"
              value={getDescricaoPassageiros()}
              fullWidth={true}
              id="outlined-start-adornment"
              sx={{ mt: 0.5, cursor: "pointer" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
                readOnly: true,
              }}
            />
          </div>
        </div>
        {/* Mantém a versão original como fallback */}
      </Box>
    );
  }

  // Versão compacta e moderna
  return (
    <Box>
      {/* Header com contador e controles */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          p: 1.5,
          backgroundColor: "grey.50",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Person color="primary" />
          <Box>
            <Typography variant="subtitle2" fontWeight="600">
              Passageiros
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Informe a idade de cada passageiro
            </Typography>
          </Box>
          <Chip
            label={`${passageirosComIdade}/${totalPassageiros}`}
            size="small"
            color={todosPreenchidos ? "success" : "warning"}
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={removerPassageiro}
            disabled={passageiros.length <= 1}
            sx={{
              backgroundColor:
                passageiros.length <= 1 ? "grey.100" : "error.light",
              "&:hover": {
                backgroundColor:
                  passageiros.length <= 1 ? "grey.100" : "error.main",
              },
              color: passageiros.length <= 1 ? "grey.400" : "white",
            }}
          >
            <Remove fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={adicionarPassageiro}
            disabled={passageiros.length >= 7}
            sx={{
              backgroundColor:
                passageiros.length >= 7 ? "grey.100" : "success.light",
              "&:hover": {
                backgroundColor:
                  passageiros.length >= 7 ? "grey.100" : "success.main",
              },
              color: passageiros.length >= 7 ? "grey.400" : "white",
            }}
          >
            <Add fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Alerta se não estiver tudo preenchido */}
      {!todosPreenchidos && totalPassageiros > 0 && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="500">
            Preencha a idade de todos os {totalPassageiros} passageiros
          </Typography>
        </Alert>
      )}

      {/* Campos de idade */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, color: "text.primary", fontWeight: 600 }}
        >
          Idade dos passageiros:
        </Typography>
        <Grid container spacing={1.5}>
          {passageiros.map((passageiro, index) => (
            <Grid item xs={6} sm={4} md={6} key={index}>
              <TooltipInformacao
                positionStart={PositionTooltip.top}
                descricao="Para bebês com menos de 1 ano, digite 0"
              >
                <TextField
                  label={`Passageiro ${index + 1}`}
                  value={passageiro.idade}
                  onChange={(e) => handlerChangePassageiro(index, e)}
                  size="small"
                  fullWidth
                  placeholder="Digite a idade"
                  error={passageiro.idade === ""}
                  helperText={
                    passageiro.idade === "" ? "Idade obrigatória" : " "
                  }
                  inputProps={{
                    maxLength: 2,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  InputProps={{
                    endAdornment:
                      passageiro.idade === "0" ? (
                        <InputAdornment position="end">
                          <ChildCare color="secondary" fontSize="small" />
                        </InputAdornment>
                      ) : null,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </TooltipInformacao>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Dica informativa */}
      <Box
        sx={{
          p: 1.5,
          backgroundColor: "info.light",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <ChildCare color="white" fontSize="small" />
        <Box>
          <Typography
            variant="caption"
            color="white"
            sx={{ fontWeight: "500", display: "block" }}
          >
            💡 Informe a idade de cada passageiro
          </Typography>
          <Typography
            variant="caption"
            color="white"
            sx={{ display: "block", opacity: 0.9 }}
          >
            Para bebês com menos de 1 ano, digite 0
          </Typography>
        </Box>
      </Box>

      {/* Resumo quando tudo estiver preenchido */}
      {/* {todosPreenchidos && totalPassageiros > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            backgroundColor: "success.main",
            borderRadius: 1,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="white" fontWeight="500">
            ✅ Todas as idades foram informadas! {totalPassageiros}{" "}
            passageiro(s)
          </Typography>
        </Box>
      )} */}
    </Box>
  );
}

export default PassageiroList;
